import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Divider,
  Fade,
  Slide,
} from "@mui/material";
import ScrollToTop from "./ScrollToTop";
import SEO from "../components/Layout/SEO";

import ProductList from "../components/Products/ProductList";
import { PhoneInTalk, SupportAgent } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";

const ProductsPage = () => {
  const [loaded, setLoaded] = useState(false);
  const [ctaVisible, setCtaVisible] = useState(false);

  useEffect(() => {
    // Trigger initial animation after component mounts
    setLoaded(true);

    // Set up scroll observer for CTA section
    const ctaObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setCtaVisible(true);
          ctaObserver.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    // Observe elements
    const ctaSection = document.getElementById("cta-section");

    if (ctaSection) ctaObserver.observe(ctaSection);

    return () => {
      ctaObserver.disconnect();
    };
  }, []);

  return (
    <Box>
      <SEO
        title="Our Products & Solutions"
        description="Explore ProtoDiag Solutions' comprehensive telecom products: Bulk Messaging Gateway, Voice Communications Platform, and SMS Firewall. Enterprise-grade solutions for modern communication needs."
        keywords="telecom products, bulk messaging gateway, SMS gateway, voice communications platform, IVR system, SMS firewall, telecom solutions, enterprise messaging"
        url="https://www.protodiag.com/products"
      />
      <ScrollToTop />

      {/* Main Product List Component with fade-in animation */}
      <Fade in={loaded} timeout={1000}>
        <Box>
          <ProductList />
        </Box>
      </Fade>

      {/* Call To Action Section with slide-in animation */}
      <Box id="cta-section" sx={{ py: 8, bgcolor: "#f8f9fa" }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Slide
                direction="right"
                in={ctaVisible}
                timeout={1000}
                mountOnEnter
              >
                <Box>
                  <Typography
                    variant="h3"
                    component="h2"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Ready to transform your telecom capabilities?
                  </Typography>
                  <Typography
                    variant="body1"
                    paragraph
                    sx={{ mb: 4, fontSize: "1.1rem" }}
                  >
                    Our team of experts is ready to help you find the right
                    solutions for your specific needs. Whether you're looking to
                    improve message delivery, enhance voice capabilities, or
                    secure your communication channels, we've got you covered.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        startIcon={<PhoneInTalk />}
                        sx={{
                          fontWeight: "bold",
                          px: 3,
                          transition: "transform 0.3s ease-in-out",
                          "&:hover": {
                            transform: "scale(1.05)",
                          },
                        }}
                        component={RouterLink}
                        to="/contact#contact-section"
                      >
                        Request a Consultation
                      </Button>
                    </Grid>
                    {/* <Grid item>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="large"
                        startIcon={<HelpOutline />}
                        sx={{
                          fontWeight: "bold",
                          px: 3,
                          transition: "transform 0.3s ease-in-out",
                          "&:hover": {
                            transform: "scale(1.05)",
                          },
                        }}
                      >
                        Product FAQs
                      </Button>
                    </Grid> */}
                  </Grid>
                </Box>
              </Slide>
            </Grid>
            <Grid item xs={12} md={6}>
              <Slide
                direction="left"
                in={ctaVisible}
                timeout={1000}
                mountOnEnter
              >
                <Box
                  sx={{
                    bgcolor: "white",
                    p: 4,
                    borderRadius: 3,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                    border: "1px solid #e0e0e0",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: "0 12px 28px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <SupportAgent
                      color="primary"
                      sx={{ fontSize: 40, mr: 2 }}
                    />
                    <Typography variant="h5" fontWeight="bold">
                      Contact Our Technical Team
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Sales Support
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight="medium"
                        sx={{ mb: 1 }}
                      >
                        +91 9899817807
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        For pricing and product inquiries
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Technical Support
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight="medium"
                        sx={{ mb: 1 }}
                      >
                        +91 9871532666
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        For implementation and troubleshooting
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Email
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        info@protodiag.com
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Slide>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Trust Indicators with grow animation */}
      {/* <Box id="trust-section" sx={{ py: 6, borderTop: "1px solid #e0e0e0" }}>
        <Container maxWidth="lg">
          <Fade in={trustVisible} timeout={1000}>
            <Box>
              <Typography
                variant="h5"
                align="center"
                fontWeight="bold"
                gutterBottom
              >
                Trusted by Leading Telecom Operators & Enterprises
              </Typography>
              <Typography
                variant="body1"
                align="center"
                color="text.secondary"
                paragraph
                sx={{ mb: 5 }}
              >
                Our solutions power mission-critical communications across India
                and beyond
              </Typography>
            </Box>
          </Fade>

          <Grid container spacing={4} justifyContent="center">
            {[1, 2, 3, 4, 5].map((item, index) => (
              <Grid
                item
                xs={6}
                sm={4}
                md={2}
                key={item}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                <Grow
                  in={trustVisible}
                  timeout={1000 + index * 200} // Staggered animation
                  style={{ transformOrigin: "center bottom" }}
                >
                  <Box
                    sx={{
                      width: 120,
                      height: 80,
                      bgcolor: "#f0f0f0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 1,
                      opacity: 0.6,
                      filter: "grayscale(1)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        opacity: 1,
                        filter: "grayscale(0)",
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      Client Logo
                    </Typography>
                  </Box>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box> */}
    </Box>
  );
};

export default ProductsPage;
